import { useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, FileSpreadsheet, FileDown, Download, MessageCircle, ShoppingCart } from "lucide-react";
import { API, getToken, getImpersonationToken, api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { getPerms } from "@/lib/permissions";

const REPORTS = [
  {
    key: "pontualidade",
    title: "Pontualidade da Equipe",
    description:
      "Score mensal por colaborador (no prazo, atrasados, não executados) — ideal para reuniões de equipe",
    icon: FileText,
    tone: "from-violet-50 to-white",
  },
  {
    key: "temperatura",
    title: "Controle de Temperatura",
    description:
      "Leituras de temperatura dos checklists com faixa conforme e campo de assinatura — pronto para fiscalização sanitária",
    icon: FileText,
    tone: "from-sky-50 to-white",
  },
  {
    key: "compliance",
    title: "Conformidade",
    description: "Checklists concluídos, % de conformidade média, itens não conformes",
    icon: FileText,
    tone: "from-emerald-50 to-white",
  },
  {
    key: "occurrences",
    title: "Não Conformidades",
    description: "Ocorrências operacionais, prioridades, status e responsáveis",
    icon: FileText,
    tone: "from-rose-50 to-white",
  },
  {
    key: "audits",
    title: "Auditorias",
    description: "Auditorias executadas, score médio, aprovações e reprovações",
    icon: FileText,
    tone: "from-amber-50 to-white",
  },
  {
    key: "estoque",
    title: "Estoque",
    description: "Inventário em tempo real: produtos, saldo, mínimos e vencimentos",
    icon: FileText,
    tone: "from-blue-50 to-white",
  },
  {
    key: "compras",
    title: "Sugestão de Compras",
    description:
      "Produtos com estoque baixo e a quantidade sugerida de compra, calculada pelo consumo dos últimos 30 dias",
    icon: ShoppingCart,
    tone: "from-lime-50 to-white",
  },
];

const FORMATS = [
  { value: "pdf", label: "PDF", icon: FileDown },
  { value: "xlsx", label: "Excel", icon: FileSpreadsheet },
  { value: "csv", label: "CSV", icon: FileText },
];

export default function Reports() {
  const { user } = useAuth();
  const perms = getPerms(user?.role);
  // FASE 17.2 — auditor only sees the audits report
  const reports = perms.reportsAuditsOnly
    ? REPORTS.filter((r) => r.key === "audits")
    : REPORTS;
  const [loading, setLoading] = useState({});

  const download = async (key, fmt) => {
    const id = `${key}-${fmt}`;
    setLoading((s) => ({ ...s, [id]: true }));
    try {
      const token = getImpersonationToken() || getToken();
      const resp = await fetch(`${API}/reports/${key}.${fmt}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${key}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${key}.${fmt} baixado`);
    } catch (err) {
      toast.error(err?.message || "Falha no download");
    } finally {
      setLoading((s) => ({ ...s, [id]: false }));
    }
  };

  const sendWhatsApp = async (key) => {
    const id = `${key}-wa`;
    setLoading((s) => ({ ...s, [id]: true }));
    const tid = toast.loading("Enviando resumo por WhatsApp e aguardando confirmação de entrega…");
    try {
      const { data } = await api.post("/reports/send-whatsapp", { report: key });
      const failed = data.failed || [];
      if (failed.length) {
        toast.error(
          `WhatsApp não entregue para ${failed.map((f) => f.name).join(", ")} — ${failed[0].reason}`,
          { id: tid, duration: 9000 }
        );
      } else if (data.sent > 0) {
        toast.success(`Resumo enviado por WhatsApp para: ${(data.recipients || []).join(", ")}`, { id: tid });
      } else {
        toast.warning("Nenhuma mensagem foi enviada. Verifique o telefone dos administradores.", { id: tid });
      }
      if (failed.length && data.sent > 0) {
        toast.success(`Entregue para: ${(data.recipients || []).join(", ")}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar por WhatsApp", { id: tid });
    } finally {
      setLoading((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <Layout
      title="Relatórios"
      subtitle="Exporte indicadores operacionais em PDF, Excel ou CSV"
    >
      <div data-testid="reports-page" className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {reports.map((r) => (
          <Card
            key={r.key}
            className={`p-6 rounded-xl border-stone-200 bg-gradient-to-br ${r.tone}`}
            data-testid={`report-card-${r.key}`}
          >
            <div className="h-10 w-10 rounded-md bg-emerald-700 text-white flex items-center justify-center">
              <r.icon className="h-5 w-5" />
            </div>
            <h3 className="font-display text-xl font-extrabold tracking-tight mt-4">{r.title}</h3>
            <p className="text-sm text-stone-600 font-body mt-2 leading-relaxed">{r.description}</p>
            <Badge variant="outline" className="mt-3 border-stone-300 text-xs font-body">
              Atualizado em tempo real
            </Badge>
            <div className="mt-5 flex flex-wrap gap-2">
              {FORMATS.map((f) => (
                <Button
                  key={f.value}
                  variant="outline"
                  size="sm"
                  className="border-stone-300 bg-white hover:bg-emerald-50 hover:border-emerald-200"
                  onClick={() => download(r.key, f.value)}
                  disabled={loading[`${r.key}-${f.value}`]}
                  data-testid={`report-${r.key}-${f.value}`}
                >
                  <f.icon className="h-3.5 w-3.5 mr-1.5" />
                  {loading[`${r.key}-${f.value}`] ? "Gerando…" : f.label}
                </Button>
              ))}
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => sendWhatsApp(r.key)}
                disabled={loading[`${r.key}-wa`]}
                data-testid={`report-${r.key}-whatsapp`}
              >
                <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
                {loading[`${r.key}-wa`] ? "Enviando…" : "Enviar para WhatsApp"}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Card className="mt-8 p-6 rounded-xl border-stone-200 bg-white">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-md bg-stone-900 text-emerald-400 flex items-center justify-center">
            <Download className="h-5 w-5" />
          </div>
          <div>
            <h4 className="font-display font-bold tracking-tight">Sobre os relatórios</h4>
            <p className="text-sm text-stone-600 font-body mt-1 max-w-2xl">
              Os relatórios trazem dados atuais da sua operação, escopados pela
              empresa logada. Para análises mais profundas em planilhas, prefira
              Excel; para auditorias e impressão, prefira PDF; para integração
              com outras ferramentas, use CSV. "Enviar para WhatsApp" manda um
              resumo para o WhatsApp cadastrado do administrador da empresa.
            </p>
          </div>
        </div>
      </Card>
    </Layout>
  );
}
